/**
 * KeyPlayerTray — DEV-1352. Single key-player (Leading Character) add/edit
 * slide-over for chronicles. Reuses the shared TeamMemberEditor (name, role,
 * why-relevant description, photo, external URL, optional L4E link) so editing
 * one player is identical to the bulk team editor, just isolated to one card.
 *
 * Persists via updateChronicle, merging the single edited/added credit into the
 * full teamCredits array (replace-all semantics) so every other player's fields
 * — including their description — are preserved untouched.
 *
 * Per CLAUDE.md: opaque overlay, logical properties, tokens, and no
 * backdrop-dismiss (Skip lost work to accidental dismissals).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import TeamMemberEditor, { type TeamMemberData } from '../TeamMemberEditor/TeamMemberEditor';
import {
  updateChronicle,
  type ChronicleResponse,
  type ChronicleTeamCreditResponse,
  type UpsertTeamCreditRequest,
} from '../../api/chronicles';

interface Props {
  open: boolean;
  onClose: () => void;
  chronicle: ChronicleResponse;
  /** The credit being edited, or null to add a new one. */
  editingCredit: ChronicleTeamCreditResponse | null;
  /** Label for the why-relevant field (template-specific). */
  whyLabel: string;
  whyPlaceholder: string;
  /** Fired after a successful save/delete. Caller refetches. */
  onSaved: () => void;
}

function toData(c: ChronicleTeamCreditResponse): TeamMemberData {
  return {
    id: `tm-${c.creditGuid}`,
    personName: c.personName ?? '',
    roleTitle: c.roleTitle ?? '',
    description: c.description ?? '',
    photoUrl: c.manualPhotoUrl ?? '',
    externalProfileUrl: c.externalProfileUrl ?? '',
    linkedUserGuid: c.userGuid ?? null,
    linkedUserDisplayName: c.userGuid ? (c.personName ?? null) : null,
    sortOrder: c.sortOrder,
  };
}

function blankData(): TeamMemberData {
  return {
    id: `tm-new-${Date.now()}`,
    personName: '',
    roleTitle: '',
    description: '',
    photoUrl: '',
    externalProfileUrl: '',
    linkedUserGuid: null,
    linkedUserDisplayName: null,
    sortOrder: 0,
  };
}

/** Map an existing response credit to its upsert request (preserving all fields). */
function preserve(c: ChronicleTeamCreditResponse, sortOrder: number): UpsertTeamCreditRequest {
  return {
    creditGuid: c.creditGuid,
    personName: c.personName,
    roleTitle: c.roleTitle ?? undefined,
    description: c.description ?? undefined,
    userGuid: c.userGuid ?? undefined,
    externalProfileUrl: c.externalProfileUrl ?? undefined,
    manualPhotoUrl: c.manualPhotoUrl ?? undefined,
    sortOrder,
  };
}

/** Map the edited form back to an upsert request. */
function fromData(d: TeamMemberData, creditGuid: string | undefined, sortOrder: number): UpsertTeamCreditRequest {
  const name = (d.linkedUserDisplayName || d.personName).trim();
  return {
    creditGuid,
    personName: name,
    roleTitle: d.roleTitle.trim() || undefined,
    description: (d.description ?? '').trim() || undefined,
    userGuid: d.linkedUserGuid || undefined,
    externalProfileUrl: d.externalProfileUrl.trim() || undefined,
    manualPhotoUrl: d.photoUrl.trim() || undefined,
    sortOrder,
  };
}

const KeyPlayerTray: React.FC<Props> = ({
  open,
  onClose,
  chronicle,
  editingCredit,
  whyLabel,
  whyPlaceholder,
  onSaved,
}) => {
  const { t } = useAppTranslation('pages');
  const isEdit = !!editingCredit;

  const [data, setData] = useState<TeamMemberData>(blankData());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset / preload on open transition.
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;
    setError(null);
    setSaving(false);
    setData(editingCredit ? toData(editingCredit) : blankData());
  }, [open, editingCredit]);

  const existing = useMemo(() => chronicle.teamCredits ?? [], [chronicle.teamCredits]);

  const change = (id: string, field: keyof TeamMemberData, value: string | number | null) =>
    setData((p) => (p.id === id ? { ...p, [field]: value } : p));

  const persist = async (credits: UpsertTeamCreditRequest[]) => {
    await updateChronicle(chronicle.chronicleGuid, { teamCredits: credits });
    onSaved();
    onClose();
  };

  const handleSave = async () => {
    const name = (data.linkedUserDisplayName || data.personName).trim();
    if (!name) {
      setError(t('chronicleKeyPlayers.tray.nameRequired', { defaultValue: 'A name is required.' }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let credits: UpsertTeamCreditRequest[];
      if (isEdit && editingCredit) {
        credits = existing.map((c, i) =>
          c.creditGuid === editingCredit.creditGuid
            ? fromData(data, editingCredit.creditGuid, i)
            : preserve(c, i),
        );
      } else {
        credits = [
          ...existing.map((c, i) => preserve(c, i)),
          fromData(data, undefined, existing.length),
        ];
      }
      await persist(credits);
    } catch (e: any) {
      setError(e?.message || t('chronicleKeyPlayers.tray.saveFailed', { defaultValue: 'Save failed.' }));
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !editingCredit) return;
    setSaving(true);
    setError(null);
    try {
      const credits = existing
        .filter((c) => c.creditGuid !== editingCredit.creditGuid)
        .map((c, i) => preserve(c, i));
      await persist(credits);
    } catch (e: any) {
      setError(e?.message || t('chronicleKeyPlayers.tray.saveFailed', { defaultValue: 'Save failed.' }));
      setSaving(false);
    }
  };

  if (!open) return null;

  const tray = (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
        background: 'rgba(2, 6, 23, 0.66)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit
          ? t('chronicleKeyPlayers.tray.editAria', { defaultValue: 'Edit key player' })
          : t('chronicleKeyPlayers.tray.addAria', { defaultValue: 'Add key player' })}
        style={{
          // Opaque composite per CLAUDE.md overlay rule.
          backgroundColor: 'var(--bg-primary)',
          backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
          borderInlineStart: '1px solid var(--border-default)',
          inlineSize: 'min(460px, 100%)',
          blockSize: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-12px 0 40px rgba(0,0,0,0.4)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBlock: 14,
            paddingInline: 18,
            borderBlockEnd: '1px solid var(--border-subtle)',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
            {isEdit
              ? t('chronicleKeyPlayers.tray.editTitle', { defaultValue: 'Edit key player' })
              : t('chronicleKeyPlayers.tray.addTitle', { defaultValue: 'Add key player' })}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 4 }}
          >
            ×
          </button>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          <TeamMemberEditor
            member={data}
            onChange={change}
            onRemove={() => { /* tray uses its own Delete button; row remove is a no-op */ }}
            uploadCategory="chronicle-team-photos"
            uploadEntityId={chronicle.chronicleGuid}
            showDescription
            descriptionLabel={whyLabel}
            descriptionPlaceholder={whyPlaceholder}
          />
          {error && <div style={{ color: 'var(--color-error)', fontSize: 13, marginBlockStart: 12 }}>{error}</div>}
        </div>

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            paddingBlock: 14,
            paddingInline: 18,
            borderBlockStart: '1px solid var(--border-subtle)',
          }}
        >
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-error)', background: 'transparent', color: 'var(--color-error)', cursor: 'pointer', fontWeight: 600 }}
            >
              {t('chronicleKeyPlayers.tray.delete', { defaultValue: 'Remove' })}
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--accent-primary)', color: 'var(--text-on-accent, #fff)', cursor: saving ? 'default' : 'pointer', fontWeight: 700, opacity: saving ? 0.7 : 1 }}
            >
              {saving
                ? t('chronicleKeyPlayers.tray.saving', { defaultValue: 'Saving…' })
                : t('chronicleKeyPlayers.tray.save', { defaultValue: 'Save' })}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  return createPortal(tray, document.body);
};

export default KeyPlayerTray;
